import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  ChevronRight, 
  ChevronLeft, 
  FlaskConical, 
  Package, 
  Palette, 
  Factory,
  Sparkles,
  Download,
  Loader2,
  Check,
  Wine,
  Beer,
  Zap,
  Droplets,
  Leaf,
  Coffee,
  Dumbbell,
  GlassWater,
  Upload,
  X,
  Info,
  AlertTriangle,
  FileText,
  Lock,
  ChevronDown,
  ChevronUp,
  Save
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Slider } from "@/components/ui/slider";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  refrescos: Droplets,
  energeticas: Zap,
  aguas: GlassWater,
  rtd: Package,
  kombucha: Leaf,
  te_cafe: Coffee,
  lacteas: GlassWater,
  proteicas: Dumbbell,
  cerveza: Beer,
  vino: Wine,
  licores: FlaskConical,
  personalizada: Sparkles,
};

const PROJECT_TYPES = [
  { id: "new_development", name: "Nuevo desarrollo", desc: "Crear una bebida desde cero" },
  { id: "optimize", name: "Optimizar", desc: "Mejorar una fórmula existente" },
  { id: "scale", name: "Escalar producción", desc: "Pasar de laboratorio a fábrica" },
  { id: "reformulate", name: "Reformular", desc: "Adaptar a nueva normativa o mercado" },
];

const BRAND_STYLES = [
  { id: "premium", name: "Premium" },
  { id: "minimal", name: "Minimalista" },
  { id: "natural", name: "Natural" },
  { id: "sports", name: "Deportivo" },
  { id: "luxury", name: "Lujo" },
  { id: "youth", name: "Juvenil" },
  { id: "gourmet", name: "Gourmet" },
];

const BRAND_TONES = [
  { id: "formal", name: "Formal / Corporativo" },
  { id: "innovative", name: "Innovador / Disruptivo" },
  { id: "technical", name: "Técnico / Científico" },
  { id: "friendly", name: "Cercano / Amigable" },
];

const FILE_TYPES = [
  { id: "formula", name: "Fórmula existente" },
  { id: "tech_sheet", name: "Ficha técnica" },
  { id: "ingredients", name: "Listado ingredientes" },
  { id: "nutritional", name: "Tabla nutricional" },
  { id: "process", name: "Diagrama de proceso" },
  { id: "brand_manual", name: "Manual de marca" },
  { id: "logo", name: "Logotipo" },
  { id: "label", name: "Diseño etiqueta" },
  { id: "other", name: "Otro documento" },
];

const steps = [
  { id: 1, name: "Brief Técnico", icon: FlaskConical, desc: "Definición del producto" },
  { id: 2, name: "Envase", icon: Package, desc: "Selección de packaging" },
  { id: 3, name: "Cierre", icon: Lock, desc: "Tipo de cierre" },
  { id: 4, name: "Marca", icon: Palette, desc: "Branding y documentación" },
  { id: 5, name: "Producción", icon: Factory, desc: "Volúmenes y plazos" },
];

export default function ConfiguratorPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [categories, setCategories] = useState({});
  const [packagingCatalog, setPackagingCatalog] = useState({});
  const [closuresCatalog, setClosuresCatalog] = useState({});
  const [compatibleClosures, setCompatibleClosures] = useState({});
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [projectId, setProjectId] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [helpOpen, setHelpOpen] = useState({});

  const [formData, setFormData] = useState({
    beverage: {
      project_type: "",
      category: "",
      subcategory: "",
      carbonation: "",
      co2_volumes: null,
      sugar_type: "",
      sweetener_type: "",
      sweetness_level: 5,
      main_flavor: "",
      secondary_notes: "",
      acidity: "",
      bitterness: "",
      mouthfeel: "",
      color: "",
      caffeine_mg: null,
      vitamins: [],
      electrolytes: false,
      adaptogens: "",
      protein_g: null,
      other_claims: "",
      is_alcoholic: false,
      abv: null,
      alcohol_base: "",
      alcohol_profile: "",
      countries: [],
      label_languages: [],
      allergens: [],
      clean_label: false,
      prohibited_ingredients: "",
      certifications: [],
      target_price_range: "",
      positioning: "",
      competitors: "",
      target_audience: "",
      brix_target: null,
      ph_target: null,
      preservation_method: "",
      shelf_life: "",
      aroma_type: "",
      acid_type: "",
      custom_description: "",
      lab_notes: "",
    },
    packaging: {
      container_type: "",
      sku: "",
      material: "",
      volume: "",
      neck: "",
      decoration_type: "",
      box_type: "",
    },
    closure: {
      closure_id: "",
      closure_name: "",
      pack_type: "",
    },
    brand: {
      brand_status: "",
      brand_name: "",
      claim: "",
      style: "",
      tone: "",
      colors: [],
      ai_generated: false,
      product_denomination: "",
      ingredients_list: "",
      allergens_declaration: "",
      net_content: "",
      country_origin: "",
      ean_code: "",
    },
    production: {
      moq: "",
      first_batch: "",
      annual_volume: "",
      target_date: "",
      sales_channel: "",
      countries: [],
      target_price: "",
      logistics_requirements: "",
      factory_notes: "",
    },
    attachments: [],
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    contact_company: "",
    data_consent: false,
    nda_accepted: false,
  });

  useEffect(() => {
    fetchCatalogs();
  }, []);

  useEffect(() => {
    if (formData.packaging.sku) {
      fetchCompatibleClosures(formData.packaging.sku);
    }
  }, [formData.packaging.sku]);

  const fetchCatalogs = async () => {
    try {
      const [catRes, pkgRes, clsRes] = await Promise.all([
        axios.get(`${API}/catalog/categories`),
        axios.get(`${API}/catalog/packaging`),
        axios.get(`${API}/catalog/closures`),
      ]);
      setCategories(catRes.data);
      setPackagingCatalog(pkgRes.data);
      setClosuresCatalog(clsRes.data);
    } catch (err) {
      console.error("Error fetching catalogs:", err);
    }
  };

  const fetchCompatibleClosures = async (sku) => {
    try {
      const res = await axios.get(`${API}/catalog/closures?sku=${sku}`);
      setCompatibleClosures(res.data);
    } catch (err) {
      console.error("Error fetching closures:", err);
    }
  };

  const updateFormData = useCallback((section, field, value) => {
    if (section) {
      setFormData(prev => ({
        ...prev,
        [section]: { ...prev[section], [field]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  }, []);

  const toggleArrayItem = useCallback((section, field, item) => {
    setFormData(prev => {
      const current = prev[section][field] || [];
      const updated = current.includes(item)
        ? current.filter(i => i !== item)
        : [...current, item];
      return { ...prev, [section]: { ...prev[section], [field]: updated } };
    });
  }, []);

  const selectPackaging = useCallback((sku) => {
    const pkg = packagingCatalog[sku];
    if (pkg) {
      setFormData(prev => ({
        ...prev,
        packaging: {
          ...prev.packaging,
          sku: sku,
          container_type: pkg.type,
          material: pkg.material,
          volume: pkg.volume,
          neck: pkg.neck,
        },
        closure: { closure_id: "", closure_name: "", pack_type: "" }
      }));
    }
  }, [packagingCatalog]);

  const selectClosure = useCallback((closureId) => {
    const closure = compatibleClosures[closureId] || closuresCatalog[closureId];
    if (closure) {
      setFormData(prev => ({
        ...prev,
        closure: {
          ...prev.closure,
          closure_id: closureId,
          closure_name: closure.name,
        }
      }));
    }
  }, [compatibleClosures, closuresCatalog]);

  const generateBrandName = async () => {
    if (!formData.beverage.category || !formData.beverage.main_flavor) {
      toast.error("Completa la categoría y sabor principal primero");
      return;
    }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/generate-name`, {
        category: formData.beverage.category,
        flavor_profile: formData.beverage.main_flavor,
        target_audience: formData.beverage.target_audience || "general",
        style: formData.brand.style || "premium",
        language: "es",
        count: 5
      });
      if (res.data.names && res.data.names.length > 0) {
        updateFormData("brand", "brand_name", res.data.names[0]);
        updateFormData("brand", "ai_generated", true);
        toast.success("Nombre generado con IA", {
          description: `Sugerencias: ${res.data.names.join(", ")}`
        });
      }
    } catch (err) {
      toast.error("Error al generar nombre");
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  const handleFileUpload = async (e, fileType) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!projectId) {
      // Save project first to get ID
      toast.info("Guardando proyecto para adjuntar archivos...");
      const newId = await saveProject(true);
      if (!newId) return;
    }
    
    setUploadingFile(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      formDataUpload.append("file_type", fileType);
      
      const res = await axios.post(`${API}/projects/${projectId}/files`, formDataUpload, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, { 
          id: res.data.id, 
          filename: file.name, 
          file_type: fileType 
        }]
      }));
      toast.success(`Archivo "${file.name}" subido`);
    } catch (err) {
      toast.error("Error al subir archivo");
      console.error(err);
    } finally {
      setUploadingFile(false);
    }
  };

  const removeAttachment = async (fileId) => {
    if (!projectId) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/files/${fileId}`);
      setFormData(prev => ({
        ...prev,
        attachments: prev.attachments.filter(a => a.id !== fileId)
      }));
      toast.success("Archivo eliminado");
    } catch (err) {
      toast.error("Error al eliminar archivo");
    }
  };

  const saveProject = async (isDraft = true) => {
    try {
      const payload = {
        ...formData,
        is_draft: isDraft,
      };
      
      if (projectId) {
        await axios.patch(`${API}/projects/${projectId}`, payload);
        toast.success("Proyecto guardado");
        return projectId;
      } else {
        const res = await axios.post(`${API}/projects`, payload);
        setProjectId(res.data.id);
        setWarnings(res.data.warnings || []);
        toast.success("Proyecto creado");
        return res.data.id;
      }
    } catch (err) {
      toast.error("Error al guardar proyecto");
      console.error(err);
      return null;
    }
  };

  const handleSubmit = async () => {
    if (!formData.contact_name || !formData.contact_email) {
      toast.error("Completa los datos de contacto");
      return;
    }
    if (!formData.data_consent) {
      toast.error("Debes aceptar el consentimiento de datos");
      return;
    }
    
    setSubmitting(true);
    try {
      let id = projectId;
      if (!id) {
        id = await saveProject(false);
      }
      if (id) {
        await axios.post(`${API}/projects/${id}/submit`);
        toast.success("Proyecto enviado correctamente", {
          description: "Nuestro equipo lo revisará y te contactará pronto."
        });
      }
    } catch (err) {
      toast.error("Error al enviar proyecto");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const downloadDossier = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/projects/${projectId}/dossier`, {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `dossier_${formData.brand.brand_name || "proyecto"}_${projectId.slice(0,8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Dossier descargado");
    } catch (err) {
      toast.error("Error al descargar dossier");
    } finally {
      setLoading(false);
    }
  };

  const isAlcoholic = ["cerveza", "vino", "licores", "rtd"].includes(formData.beverage.category) || formData.beverage.is_alcoholic;
  
  const filteredPackaging = Object.entries(packagingCatalog).filter(([_, pkg]) => {
    if (!formData.packaging.container_type) return true;
    return pkg.type === formData.packaging.container_type;
  });

  const toggleHelp = (id) => {
    setHelpOpen(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const HelpSection = ({ id, title, children }) => (
    <Collapsible open={helpOpen[id]} onOpenChange={() => toggleHelp(id)} className="mb-4">
      <CollapsibleTrigger className="flex items-center gap-2 text-udl-gray-400 hover:text-udl-primary text-xs">
        <Info size={14} />
        <span>{title}</span>
        {helpOpen[id] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2 p-3 bg-udl-card/50 border border-udl-gray-800 text-udl-gray-400 text-xs">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-8" data-testid="step-1-content">
            {/* Project Type */}
            <div>
              <Label className="text-udl-white mb-4 block text-sm uppercase tracking-wider">
                Tipo de proyecto
              </Label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {PROJECT_TYPES.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => updateFormData("beverage", "project_type", type.id)}
                    data-testid={`project-type-${type.id}`}
                    className={`p-4 border text-left transition-colors ${
                      formData.beverage.project_type === type.id
                        ? "bg-udl-primary/10 border-udl-primary"
                        : "bg-udl-card border-udl-gray-800 hover:border-udl-gray-700"
                    }`}
                  >
                    <p className={`font-medium ${formData.beverage.project_type === type.id ? "text-udl-primary" : "text-udl-white"}`}>
                      {type.name}
                    </p>
                    <p className="text-udl-gray-400 text-xs mt-1">{type.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Category Selection */}
            <div>
              <Label className="text-udl-white mb-4 block text-sm uppercase tracking-wider">
                Categoría de bebida
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {Object.entries(categories).map(([id, cat]) => {
                  const IconComponent = CATEGORY_ICONS[id] || FlaskConical;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => {
                        updateFormData("beverage", "category", id);
                        updateFormData("beverage", "subcategory", "");
                        updateFormData("beverage", "is_alcoholic", ["cerveza", "vino", "licores"].includes(id));
                      }}
                      data-testid={`category-${id}`}
                      className={`p-4 border text-left transition-colors ${
                        formData.beverage.category === id
                          ? "bg-udl-primary/10 border-udl-primary"
                          : "bg-udl-card border-udl-gray-800 hover:border-udl-gray-700"
                      }`}
                    >
                      <IconComponent size={24} className={formData.beverage.category === id ? "text-udl-primary" : "text-udl-gray-400"} />
                      <p className={`font-medium mt-2 ${formData.beverage.category === id ? "text-udl-primary" : "text-udl-white"}`}>
                        {cat.name}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Subcategory */}
            {formData.beverage.category && categories[formData.beverage.category]?.subcategories?.length > 0 && (
              <div>
                <Label className="text-udl-gray-400 mb-2 block">Subcategoría</Label>
                <Select 
                  value={formData.beverage.subcategory} 
                  onValueChange={(v) => updateFormData("beverage", "subcategory", v)}
                >
                  <SelectTrigger className="bg-udl-surface border-udl-gray-800 text-udl-white" data-testid="subcategory-select">
                    <SelectValue placeholder="Seleccionar subcategoría" />
                  </SelectTrigger>
                  <SelectContent className="bg-udl-surface border-udl-gray-800">
                    {categories[formData.beverage.category].subcategories.map(sub => (
                      <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Carbonation */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label className="text-udl-gray-400 mb-2 block">Carbonatación</Label>
                <Select 
                  value={formData.beverage.carbonation} 
                  onValueChange={(v) => updateFormData("beverage", "carbonation", v)}
                >
                  <SelectTrigger className="bg-udl-surface border-udl-gray-800 text-udl-white" data-testid="carbonation-select">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent className="bg-udl-surface border-udl-gray-800">
                    <SelectItem value="none">Sin gas</SelectItem>
                    <SelectItem value="low">Baja (1.5-2.5 vol)</SelectItem>
                    <SelectItem value="medium">Media (2.5-3.5 vol)</SelectItem>
                    <SelectItem value="high">Alta (3.5-4.5 vol)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.beverage.carbonation && formData.beverage.carbonation !== "none" && (
                <div>
                  <Label className="text-udl-gray-400 mb-2 block">Volúmenes CO2 (opcional)</Label>
                  <Input 
                    type="number"
                    step="0.1"
                    min="0"
                    max="5"
                    value={formData.beverage.co2_volumes || ""}
                    onChange={(e) => updateFormData("beverage", "co2_volumes", parseFloat(e.target.value) || null)}
                    className="bg-udl-surface border-udl-gray-800 text-udl-white"
                    placeholder="Ej: 3.2"
                    data-testid="co2-input"
                  />
                </div>
              )}
            </div>

            {/* Sweetness */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label className="text-udl-gray-400 mb-2 block">Azúcar</Label>
                <Select 
                  value={formData.beverage.sugar_type} 
                  onValueChange={(v) => updateFormData("beverage", "sugar_type", v)}
                >
                  <SelectTrigger className="bg-udl-surface border-udl-gray-800 text-udl-white" data-testid="sugar-select">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent className="bg-udl-surface border-udl-gray-800">
                    <SelectItem value="with_sugar">Con azúcar</SelectItem>
                    <SelectItem value="no_sugar">Sin azúcar (Zero)</SelectItem>
                    <SelectItem value="reduced">Bajo en azúcar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.beverage.sugar_type === "no_sugar" && (
                <div>
                  <Label className="text-udl-gray-400 mb-2 block">Tipo de edulcorante</Label>
                  <Input 
                    value={formData.beverage.sweetener_type}
                    onChange={(e) => updateFormData("beverage", "sweetener_type", e.target.value)}
                    className="bg-udl-surface border-udl-gray-800 text-udl-white"
                    placeholder="Ej: Stevia, Eritritol, Sucralosa"
                    data-testid="sweetener-input"
                  />
                </div>
              )}
            </div>

            {/* Sweetness Level Slider */}
            <div>
              <div className="flex justify-between mb-2">
                <Label className="text-udl-gray-400">Nivel de dulzor</Label>
                <span className="text-udl-primary text-sm">{formData.beverage.sweetness_level}/10</span>
              </div>
              <Slider
                value={[formData.beverage.sweetness_level]}
                onValueChange={([v]) => updateFormData("beverage", "sweetness_level", v)}
                max={10}
                min={1}
                step={1}
                className="py-4"
                data-testid="sweetness-slider"
              />
              <div className="flex justify-between text-udl-gray-700 text-xs">
                <span>Muy seco</span>
                <span>Muy dulce</span>
              </div>
            </div>

            {/* Flavor Profile */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label className="text-udl-gray-400 mb-2 block">Sabor principal</Label>
                <Input 
                  value={formData.beverage.main_flavor}
                  onChange={(e) => updateFormData("beverage", "main_flavor", e.target.value)}
                  className="bg-udl-surface border-udl-gray-800 text-udl-white"
                  placeholder="Ej: Limón, Tropical, Frutos rojos"
                  data-testid="main-flavor-input"
                />
              </div>
              <div>
                <Label className="text-udl-gray-400 mb-2 block">Notas secundarias</Label>
                <Input 
                  value={formData.beverage.secondary_notes}
                  onChange={(e) => updateFormData("beverage", "secondary_notes", e.target.value)}
                  className="bg-udl-surface border-udl-gray-800 text-udl-white"
                  placeholder="Ej: Jengibre, Menta, Hierba luisa"
                  data-testid="secondary-notes-input"
                />
              </div>
            </div>

            {/* Organoleptic */}
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <Label className="text-udl-gray-400 mb-2 block">Acidez</Label>
                <Select 
                  value={formData.beverage.acidity} 
                  onValueChange={(v) => updateFormData("beverage", "acidity", v)}
                >
                  <SelectTrigger className="bg-udl-surface border-udl-gray-800 text-udl-white" data-testid="acidity-select">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent className="bg-udl-surface border-udl-gray-800">
                    <SelectItem value="low">Baja</SelectItem>
                    <SelectItem value="medium">Media</SelectItem>
                    <SelectItem value="high">Alta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-udl-gray-400 mb-2 block">Amargor</Label>
                <Select 
                  value={formData.beverage.bitterness} 
                  onValueChange={(v) => updateFormData("beverage", "bitterness", v)}
                >
                  <SelectTrigger className="bg-udl-surface border-udl-gray-800 text-udl-white" data-testid="bitterness-select">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent className="bg-udl-surface border-udl-gray-800">
                    <SelectItem value="low">Bajo</SelectItem>
                    <SelectItem value="medium">Medio</SelectItem>
                    <SelectItem value="high">Alto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-udl-gray-400 mb-2 block">Sensación en boca</Label>
                <Select 
                  value={formData.beverage.mouthfeel} 
                  onValueChange={(v) => updateFormData("beverage", "mouthfeel", v)}
                >
                  <SelectTrigger className="bg-udl-surface border-udl-gray-800 text-udl-white" data-testid="mouthfeel-select">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent className="bg-udl-surface border-udl-gray-800">
                    <SelectItem value="light">Ligera / Refrescante</SelectItem>
                    <SelectItem value="medium">Media / Equilibrada</SelectItem>
                    <SelectItem value="dense">Densa / Cremosa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Functionality */}
            <HelpSection id="functionality" title="¿Qué son los ingredientes funcionales?">
              Los ingredientes funcionales aportan beneficios adicionales a la bebida más allá de la hidratación: energía, vitaminas, proteínas, etc.
            </HelpSection>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label className="text-udl-gray-400 mb-2 block">Cafeína (mg por porción)</Label>
                <Input 
                  type="number"
                  min="0"
                  max="500"
                  value={formData.beverage.caffeine_mg || ""}
                  onChange={(e) => updateFormData("beverage", "caffeine_mg", parseFloat(e.target.value) || null)}
                  className="bg-udl-surface border-udl-gray-800 text-udl-white"
                  placeholder="Ej: 80"
                  data-testid="caffeine-input"
                />
              </div>
              <div>
                <Label className="text-udl-gray-400 mb-2 block">Proteína (g por porción)</Label>
                <Input 
                  type="number"
                  min="0"
                  max="100"
                  value={formData.beverage.protein_g || ""}
                  onChange={(e) => updateFormData("beverage", "protein_g", parseFloat(e.target.value) || null)}
                  className="bg-udl-surface border-udl-gray-800 text-udl-white"
                  placeholder="Ej: 20"
                  data-testid="protein-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-udl-gray-400 mb-3 block">Vitaminas</Label>
              <div className="flex flex-wrap gap-2">
                {["B1", "B2", "B3", "B5", "B6", "B9", "B12", "C", "D", "E", "K"].map((vit) => (
                  <button
                    key={vit}
                    type="button"
                    onClick={() => toggleArrayItem("beverage", "vitamins", vit)}
                    className={`px-3 py-1.5 text-xs font-medium border transition-colors ${
                      formData.beverage.vitamins.includes(vit)
                        ? "bg-udl-primary/20 border-udl-primary text-udl-primary"
                        : "border-udl-gray-800 text-udl-gray-400 hover:border-udl-gray-700"
                    }`}
                  >
                    Vit. {vit}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="electrolytes"
                  checked={formData.beverage.electrolytes}
                  onCheckedChange={(v) => updateFormData("beverage", "electrolytes", v)}
                  data-testid="electrolytes-checkbox"
                />
                <Label htmlFor="electrolytes" className="text-udl-gray-400 text-sm cursor-pointer">Con electrolitos</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="clean_label"
                  checked={formData.beverage.clean_label}
                  onCheckedChange={(v) => updateFormData("beverage", "clean_label", v)}
                  data-testid="clean-label-checkbox"
                />
                <Label htmlFor="clean_label" className="text-udl-gray-400 text-sm cursor-pointer">Clean label</Label>
              </div>
            </div>

            {/* Alcohol */}
            {isAlcoholic && (
              <div className="border-t border-udl-gray-800 pt-8">
                <h3 className="text-udl-white font-heading font-bold mb-6">Información alcohólica</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <Label className="text-udl-gray-400 mb-2 block">Graduación (%ABV)</Label>
                    <Input 
                      type="number"
                      step="0.1"
                      min="0"
                      max="80"
                      value={formData.beverage.abv || ""}
                      onChange={(e) => updateFormData("beverage", "abv", parseFloat(e.target.value) || null)}
                      className="bg-udl-surface border-udl-gray-800 text-udl-white"
                      placeholder="Ej: 5.0"
                      data-testid="abv-input"
                    />
                  </div>
                  <div>
                    <Label className="text-udl-gray-400 mb-2 block">Base alcohólica</Label>
                    <Select 
                      value={formData.beverage.alcohol_base} 
                      onValueChange={(v) => updateFormData("beverage", "alcohol_base", v)}
                    >
                      <SelectTrigger className="bg-udl-surface border-udl-gray-800 text-udl-white" data-testid="alcohol-base-select">
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent className="bg-udl-surface border-udl-gray-800">
                        <SelectItem value="beer">Cerveza</SelectItem>
                        <SelectItem value="wine">Vino</SelectItem>
                        <SelectItem value="vodka">Vodka</SelectItem>
                        <SelectItem value="gin">Gin</SelectItem>
                        <SelectItem value="rum">Ron</SelectItem>
                        <SelectItem value="tequila">Tequila</SelectItem>
                        <SelectItem value="whisky">Whisky</SelectItem>
                        <SelectItem value="neutral">Alcohol neutro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-udl-gray-400 mb-2 block">Perfil</Label>
                    <Select 
                      value={formData.beverage.alcohol_profile} 
                      onValueChange={(v) => updateFormData("beverage", "alcohol_profile", v)}
                    >
                      <SelectTrigger className="bg-udl-surface border-udl-gray-800 text-udl-white" data-testid="alcohol-profile-select">
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent className="bg-udl-surface border-udl-gray-800">
                        <SelectItem value="dry">Seco</SelectItem>
                        <SelectItem value="sweet">Dulce</SelectItem>
                        <SelectItem value="bitter">Amargo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Regulatory */}
            <div className="border-t border-udl-gray-800 pt-8">
              <h3 className="text-udl-white font-heading font-bold mb-6">Requisitos regulatorios</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-udl-gray-400 mb-2 block">Países de comercialización</Label>
                  <Input 
                    value={formData.beverage.countries.join(", ")}
                    onChange={(e) => updateFormData("beverage", "countries", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
                    className="bg-udl-surface border-udl-gray-800 text-udl-white"
                    placeholder="España, Francia, Alemania"
                    data-testid="countries-input"
                  />
                </div>
                <div>
                  <Label className="text-udl-gray-400 mb-2 block">Idiomas de etiqueta</Label>
                  <Input 
                    value={formData.beverage.label_languages.join(", ")}
                    onChange={(e) => updateFormData("beverage", "label_languages", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
                    className="bg-udl-surface border-udl-gray-800 text-udl-white"
                    placeholder="ES, EN, FR"
                    data-testid="languages-input"
                  />
                </div>
              </div>
              
              <div className="mt-6">
                <Label className="text-udl-gray-400 mb-3 block">Certificaciones deseadas</Label>
                <div className="flex flex-wrap gap-2">
                  {["Ecológico/Bio", "Vegano", "Sin gluten", "Kosher", "Halal", "Non-GMO", "Fair Trade"].map((cert) => (
                    <button
                      key={cert}
                      type="button"
                      onClick={() => toggleArrayItem("beverage", "certifications", cert)}
                      className={`px-3 py-1.5 text-xs font-medium border transition-colors ${
                        formData.beverage.certifications.includes(cert)
                          ? "bg-udl-primary/20 border-udl-primary text-udl-primary"
                          : "border-udl-gray-800 text-udl-gray-400 hover:border-udl-gray-700"
                      }`}
                    >
                      {cert}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Positioning */}
            <div className="border-t border-udl-gray-800 pt-8">
              <h3 className="text-udl-white font-heading font-bold mb-6">Posicionamiento</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-udl-gray-400 mb-2 block">Segmento de precio</Label>
                  <Select 
                    value={formData.beverage.positioning} 
                    onValueChange={(v) => updateFormData("beverage", "positioning", v)}
                  >
                    <SelectTrigger className="bg-udl-surface border-udl-gray-800 text-udl-white" data-testid="positioning-select">
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent className="bg-udl-surface border-udl-gray-800">
                      <SelectItem value="value">Value (económico)</SelectItem>
                      <SelectItem value="mainstream">Mainstream (medio)</SelectItem>
                      <SelectItem value="premium">Premium</SelectItem>
                      <SelectItem value="luxury">Luxury</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-udl-gray-400 mb-2 block">Público objetivo</Label>
                  <Input 
                    value={formData.beverage.target_audience}
                    onChange={(e) => updateFormData("beverage", "target_audience", e.target.value)}
                    className="bg-udl-surface border-udl-gray-800 text-udl-white"
                    placeholder="Ej: Jóvenes deportistas 18-35"
                    data-testid="target-audience-input"
                  />
                </div>
              </div>
              <div className="mt-6">
                <Label className="text-udl-gray-400 mb-2 block">Competidores de referencia</Label>
                <Input 
                  value={formData.beverage.competitors}
                  onChange={(e) => updateFormData("beverage", "competitors", e.target.value)}
                  className="bg-udl-surface border-udl-gray-800 text-udl-white"
                  placeholder="Ej: Red Bull, Monster, Celsius"
                  data-testid="competitors-input"
                />
              </div>
            </div>

            {/* Custom Description */}
            <div className="border-t border-udl-gray-800 pt-8">
              <Label className="text-udl-gray-400 mb-2 block">Descripción adicional del proyecto</Label>
              <Textarea 
                value={formData.beverage.custom_description}
                onChange={(e) => updateFormData("beverage", "custom_description", e.target.value)}
                className="bg-udl-surface border-udl-gray-800 text-udl-white min-h-[120px]"
                placeholder="Describe tu visión del producto, inspiraciones, objetivos específicos..."
                data-testid="description-textarea"
              />
            </div>

            {/* File Uploads */}
            <div className="border-t border-udl-gray-800 pt-8">
              <h3 className="text-udl-white font-heading font-bold mb-4">Documentación técnica</h3>
              <HelpSection id="files" title="¿Qué documentos puedo adjuntar?">
                Puedes adjuntar fórmulas existentes, fichas técnicas, listados de ingredientes, tablas nutricionales, diagramas de proceso, etc. 
                Esta información nos ayuda a entender mejor tu proyecto.
              </HelpSection>
              
              <div className="grid md:grid-cols-2 gap-4">
                {FILE_TYPES.slice(0, 5).map((ft) => (
                  <div key={ft.id} className="flex items-center gap-3">
                    <label 
                      className="flex-1 flex items-center gap-3 p-3 bg-udl-card border border-udl-gray-800 cursor-pointer hover:border-udl-gray-700 transition-colors"
                    >
                      <Upload size={18} className="text-udl-gray-400" />
                      <span className="text-udl-gray-400 text-sm">{ft.name}</span>
                      <input 
                        type="file" 
                        className="hidden" 
                        onChange={(e) => handleFileUpload(e, ft.id)}
                        disabled={uploadingFile}
                      />
                    </label>
                  </div>
                ))}
              </div>
              
              {/* Uploaded files list */}
              {formData.attachments.length > 0 && (
                <div className="mt-4 space-y-2">
                  <Label className="text-udl-gray-400 text-sm">Archivos adjuntos:</Label>
                  {formData.attachments.map((att) => (
                    <div key={att.id} className="flex items-center justify-between p-2 bg-udl-surface border border-udl-gray-800">
                      <div className="flex items-center gap-2">
                        <FileText size={16} className="text-udl-primary" />
                        <span className="text-udl-white text-sm">{att.filename}</span>
                        <span className="text-udl-gray-700 text-xs">({att.file_type})</span>
                      </div>
                      <button 
                        onClick={() => removeAttachment(att.id)}
                        className="text-udl-gray-400 hover:text-red-400"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-8" data-testid="step-2-content">
            {/* Container Type */}
            <div>
              <Label className="text-udl-white mb-4 block text-sm uppercase tracking-wider">
                Tipo de envase
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { id: "glass", name: "Vidrio", desc: "Premium, reciclable, barrera total", icon: Wine },
                  { id: "pet", name: "PET", desc: "Ligero, económico, versátil", icon: GlassWater },
                  { id: "can", name: "Lata", desc: "Moderno, portátil, reciclable", icon: Package },
                  { id: "bib", name: "Bag in Box", desc: "Alto volumen, horeca", icon: Package },
                ].map((type) => {
                  const IconComponent = type.icon;
                  return (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => {
                        updateFormData("packaging", "container_type", type.id);
                        updateFormData("packaging", "sku", "");
                      }}
                      data-testid={`container-${type.id}`}
                      className={`p-6 border text-left transition-colors ${
                        formData.packaging.container_type === type.id
                          ? "bg-udl-primary/10 border-udl-primary"
                          : "bg-udl-card border-udl-gray-800 hover:border-udl-gray-700"
                      }`}
                    >
                      <IconComponent className={formData.packaging.container_type === type.id ? "text-udl-primary" : "text-udl-gray-400"} size={28} />
                      <p className="text-udl-white font-medium mt-3">{type.name}</p>
                      <p className="text-udl-gray-400 text-xs mt-1">{type.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Packaging Catalog */}
            {formData.packaging.container_type && (
              <div>
                <Label className="text-udl-white mb-4 block text-sm uppercase tracking-wider">
                  Selecciona un formato específico
                </Label>
                <ScrollArea className="h-[600px]">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pr-4">
                    {filteredPackaging.map(([sku, pkg]) => (
                      <button
                        key={sku}
                        type="button"
                        onClick={() => selectPackaging(sku)}
                        data-testid={`packaging-${sku}`}
                        className={`relative overflow-hidden border text-left transition-colors ${
                          formData.packaging.sku === sku
                            ? "border-udl-primary bg-udl-primary/5"
                            : "border-udl-gray-800 hover:border-udl-gray-700"
                        }`}
                      >
                        {formData.packaging.sku === sku && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-udl-primary flex items-center justify-center z-10">
                            <Check size={14} className="text-udl-bg" />
                          </div>
                        )}
                        {pkg.eco_friendly && (
                          <div className="absolute top-2 left-2 px-2 py-0.5 bg-green-500/90 text-white text-xs font-medium z-10">
                            ECO
                          </div>
                        )}
                        <div className="aspect-[4/3] bg-udl-surface relative">
                          <img 
                            src={pkg.image} 
                            alt={pkg.subtype} 
                            className="w-full h-full object-cover opacity-80"
                          />
                          {/* Volume badge on image */}
                          <div className="absolute bottom-2 left-2 px-2 py-1 bg-udl-bg/90 border border-udl-primary">
                            <span className="text-udl-primary font-bold text-lg">{pkg.volume}</span>
                          </div>
                        </div>
                        <div className="p-4 bg-udl-card">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <p className="text-udl-white font-medium text-sm">{pkg.subtype}</p>
                              <p className="text-udl-gray-700 text-xs font-mono mt-0.5">{sku}</p>
                            </div>
                          </div>
                          
                          {/* Technical specs grid */}
                          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs mb-3">
                            <div>
                              <span className="text-udl-gray-700">Material:</span>
                              <span className="text-udl-gray-400 ml-1">{pkg.material}</span>
                            </div>
                            <div>
                              <span className="text-udl-gray-700">Cuello:</span>
                              <span className="text-udl-gray-400 ml-1">{pkg.neck}</span>
                            </div>
                            {pkg.height_mm && (
                              <div>
                                <span className="text-udl-gray-700">Altura:</span>
                                <span className="text-udl-gray-400 ml-1">{pkg.height_mm} mm</span>
                              </div>
                            )}
                            {pkg.weight_gr && (
                              <div>
                                <span className="text-udl-gray-700">Peso:</span>
                                <span className="text-udl-gray-400 ml-1">{pkg.weight_gr} g</span>
                              </div>
                            )}
                            {pkg.diameter_mm && (
                              <div className="col-span-2">
                                <span className="text-udl-gray-700">Diámetro:</span>
                                <span className="text-udl-gray-400 ml-1">{pkg.diameter_mm} mm</span>
                              </div>
                            )}
                          </div>
                          
                          <p className="text-udl-gray-400 text-xs mb-3 border-t border-udl-gray-800 pt-2">
                            <span className="text-udl-gray-700">Uso:</span> {pkg.use}
                          </p>
                          
                          {/* Compatibility badges */}
                          <div className="flex flex-wrap gap-1">
                            {pkg.carbonation_compatible && (
                              <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 text-xs">CO2</span>
                            )}
                            {pkg.hot_fill_compatible && (
                              <span className="px-1.5 py-0.5 bg-orange-500/20 text-orange-400 text-xs">Hot-fill</span>
                            )}
                            {pkg.spirits_compatible && (
                              <span className="px-1.5 py-0.5 bg-purple-500/20 text-purple-400 text-xs">Spirits</span>
                            )}
                            {pkg.wine_compatible && (
                              <span className="px-1.5 py-0.5 bg-red-500/20 text-red-400 text-xs">Vino</span>
                            )}
                            {pkg.beer_compatible && (
                              <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 text-xs">Cerveza</span>
                            )}
                            {pkg.sparkling && (
                              <span className="px-1.5 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs">Espumoso</span>
                            )}
                            {pkg.decanter && (
                              <span className="px-1.5 py-0.5 bg-pink-500/20 text-pink-400 text-xs">Decanter</span>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}

            {/* Decoration Type */}
            {formData.packaging.sku && (
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-udl-gray-400 mb-2 block">Tipo de decoración</Label>
                  <Select 
                    value={formData.packaging.decoration_type} 
                    onValueChange={(v) => updateFormData("packaging", "decoration_type", v)}
                  >
                    <SelectTrigger className="bg-udl-surface border-udl-gray-800 text-udl-white" data-testid="decoration-select">
                      <SelectValue placeholder="Seleccionar decoración" />
                    </SelectTrigger>
                    <SelectContent className="bg-udl-surface border-udl-gray-800">
                      <SelectItem value="adhesive">Etiqueta adhesiva</SelectItem>
                      <SelectItem value="sleeve">Sleeve retráctil</SelectItem>
                      <SelectItem value="screen-print">Serigrafía directa</SelectItem>
                      <SelectItem value="embossed">Grabado/Relieve</SelectItem>
                      <SelectItem value="neck-label">Collarín</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-udl-gray-400 mb-2 block">Tipo de embalaje</Label>
                  <Select 
                    value={formData.packaging.box_type} 
                    onValueChange={(v) => updateFormData("packaging", "box_type", v)}
                  >
                    <SelectTrigger className="bg-udl-surface border-udl-gray-800 text-udl-white" data-testid="box-type-select">
                      <SelectValue placeholder="Seleccionar embalaje" />
                    </SelectTrigger>
                    <SelectContent className="bg-udl-surface border-udl-gray-800">
                      <SelectItem value="tray-shrink">Bandeja + retráctil</SelectItem>
                      <SelectItem value="carton">Caja de cartón</SelectItem>
                      <SelectItem value="cluster">Cluster pack</SelectItem>
                      <SelectItem value="display">Display / Expositor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Warnings */}
            {warnings.length > 0 && (
              <div className="p-4 bg-yellow-500/10 border border-yellow-500/30">
                <div className="flex items-center gap-2 text-yellow-400 mb-2">
                  <AlertTriangle size={18} />
                  <span className="font-medium">Alertas de compatibilidad</span>
                </div>
                {warnings.map((w, i) => (
                  <p key={i} className="text-yellow-400/80 text-sm">{w}</p>
                ))}
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-8" data-testid="step-3-content">
            {!formData.packaging.sku ? (
              <div className="text-center py-12 bg-udl-surface border border-udl-gray-800">
                <Lock className="mx-auto text-udl-gray-700 mb-4" size={48} />
                <p className="text-udl-gray-400">Selecciona primero un envase en el paso anterior</p>
              </div>
            ) : (
              <>
                <div>
                  <Label className="text-udl-white mb-4 block text-sm uppercase tracking-wider">
                    Cierre compatible con {packagingCatalog[formData.packaging.sku]?.subtype}
                  </Label>
                  <p className="text-udl-gray-400 text-sm mb-6">
                    Cuello: <span className="text-udl-primary">{formData.packaging.neck}</span>
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Object.entries(compatibleClosures).map(([id, closure]) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => selectClosure(id)}
                        data-testid={`closure-${id}`}
                        className={`p-4 border text-left transition-colors ${
                          formData.closure.closure_id === id
                            ? "bg-udl-primary/10 border-udl-primary"
                            : "bg-udl-card border-udl-gray-800 hover:border-udl-gray-700"
                        }`}
                      >
                        {formData.closure.closure_id === id && (
                          <div className="float-right">
                            <Check size={18} className="text-udl-primary" />
                          </div>
                        )}
                        <p className={`font-medium ${formData.closure.closure_id === id ? "text-udl-primary" : "text-udl-white"}`}>
                          {closure.name}
                        </p>
                        <p className="text-udl-gray-700 text-xs mt-1 font-mono">{id}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Pack Type for cans */}
                {formData.packaging.container_type === "can" && (
                  <div>
                    <Label className="text-udl-gray-400 mb-2 block">Formato de pack</Label>
                    <Select 
                      value={formData.closure.pack_type} 
                      onValueChange={(v) => updateFormData("closure", "pack_type", v)}
                    >
                      <SelectTrigger className="bg-udl-surface border-udl-gray-800 text-udl-white" data-testid="pack-type-select">
                        <SelectValue placeholder="Seleccionar formato" />
                      </SelectTrigger>
                      <SelectContent className="bg-udl-surface border-udl-gray-800">
                        <SelectItem value="tray">Bandeja</SelectItem>
                        <SelectItem value="shrink">Retráctil 4/6 unidades</SelectItem>
                        <SelectItem value="box">Caja de cartón</SelectItem>
                        <SelectItem value="single">Unidad suelta</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}
          </div>
        );

      case 4:
        return (
          <div className="space-y-8" data-testid="step-4-content">
            {/* Brand Status */}
            <div>
              <Label className="text-udl-white mb-4 block text-sm uppercase tracking-wider">
                Estado de tu marca
              </Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { id: "have_brand", name: "Tengo marca", desc: "Ya tengo nombre, logo y diseño" },
                  { id: "have_name_no_design", name: "Tengo nombre", desc: "Tengo nombre pero necesito diseño" },
                  { id: "need_everything", name: "Empiezo de cero", desc: "Necesito nombre y diseño completo" },
                ].map((status) => (
                  <button
                    key={status.id}
                    type="button"
                    onClick={() => updateFormData("brand", "brand_status", status.id)}
                    data-testid={`brand-status-${status.id}`}
                    className={`p-4 border text-left transition-colors ${
                      formData.brand.brand_status === status.id
                        ? "bg-udl-primary/10 border-udl-primary"
                        : "bg-udl-card border-udl-gray-800 hover:border-udl-gray-700"
                    }`}
                  >
                    <p className={`font-medium ${formData.brand.brand_status === status.id ? "text-udl-primary" : "text-udl-white"}`}>
                      {status.name}
                    </p>
                    <p className="text-udl-gray-400 text-xs mt-1">{status.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Brand Name */}
            <div>
              <Label className="text-udl-white mb-4 block text-sm uppercase tracking-wider">
                Nombre de marca
              </Label>
              <div className="flex gap-4">
                <Input 
                  value={formData.brand.brand_name}
                  onChange={(e) => {
                    updateFormData("brand", "brand_name", e.target.value);
                    updateFormData("brand", "ai_generated", false);
                  }}
                  className="flex-1 bg-udl-surface border-udl-gray-800 text-udl-white"
                  placeholder="Introduce el nombre de tu marca"
                  data-testid="brand-name-input"
                />
                <button
                  type="button"
                  onClick={generateBrandName}
                  disabled={generating}
                  className="btn-secondary flex items-center gap-2 whitespace-nowrap"
                  data-testid="generate-name-btn"
                >
                  {generating ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />}
                  {generating ? "Generando..." : "Generar con IA"}
                </button>
              </div>
              {formData.brand.ai_generated && (
                <p className="text-udl-primary text-xs mt-2 flex items-center gap-1">
                  <Sparkles size={12} /> Nombre generado por IA (GPT-5.2)
                </p>
              )}
            </div>

            {/* Claim */}
            <div>
              <Label className="text-udl-gray-400 mb-2 block">Claim / Eslogan</Label>
              <Input 
                value={formData.brand.claim}
                onChange={(e) => updateFormData("brand", "claim", e.target.value)}
                className="bg-udl-surface border-udl-gray-800 text-udl-white"
                placeholder="Ej: Tu energía natural, Sabor auténtico"
                data-testid="claim-input"
              />
            </div>

            {/* Brand Style */}
            <div>
              <Label className="text-udl-white mb-4 block text-sm uppercase tracking-wider">
                Estilo de marca
              </Label>
              <div className="flex flex-wrap gap-2">
                {BRAND_STYLES.map((style) => (
                  <button
                    key={style.id}
                    type="button"
                    onClick={() => updateFormData("brand", "style", style.id)}
                    data-testid={`style-${style.id}`}
                    className={`px-4 py-2 text-sm font-medium border transition-colors ${
                      formData.brand.style === style.id
                        ? "bg-udl-primary/10 border-udl-primary text-udl-primary"
                        : "border-udl-gray-800 text-udl-gray-400 hover:border-udl-gray-700"
                    }`}
                  >
                    {style.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Tone */}
            <div>
              <Label className="text-udl-gray-400 mb-2 block">Tono de comunicación</Label>
              <Select 
                value={formData.brand.tone} 
                onValueChange={(v) => updateFormData("brand", "tone", v)}
              >
                <SelectTrigger className="bg-udl-surface border-udl-gray-800 text-udl-white" data-testid="tone-select">
                  <SelectValue placeholder="Seleccionar tono" />
                </SelectTrigger>
                <SelectContent className="bg-udl-surface border-udl-gray-800">
                  {BRAND_TONES.map(tone => (
                    <SelectItem key={tone.id} value={tone.id}>{tone.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* File uploads for brand */}
            {formData.brand.brand_status === "have_brand" && (
              <div className="border-t border-udl-gray-800 pt-8">
                <h3 className="text-udl-white font-heading font-bold mb-4">Sube tus assets de marca</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {FILE_TYPES.slice(5, 8).map((ft) => (
                    <label 
                      key={ft.id}
                      className="flex items-center gap-3 p-3 bg-udl-card border border-udl-gray-800 cursor-pointer hover:border-udl-gray-700 transition-colors"
                    >
                      <Upload size={18} className="text-udl-gray-400" />
                      <span className="text-udl-gray-400 text-sm">{ft.name}</span>
                      <input 
                        type="file" 
                        className="hidden" 
                        onChange={(e) => handleFileUpload(e, ft.id)}
                        disabled={uploadingFile}
                      />
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Legal Content Checklist */}
            <div className="border-t border-udl-gray-800 pt-8">
              <h3 className="text-udl-white font-heading font-bold mb-4">Contenido legal de etiqueta</h3>
              <HelpSection id="legal" title="¿Qué información es obligatoria?">
                La normativa europea requiere: denominación del producto, lista de ingredientes, alérgenos destacados, contenido neto, 
                país de origen, fecha de caducidad y datos del fabricante/importador.
              </HelpSection>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-udl-gray-400 mb-2 block">Denominación del producto</Label>
                  <Input 
                    value={formData.brand.product_denomination}
                    onChange={(e) => updateFormData("brand", "product_denomination", e.target.value)}
                    className="bg-udl-surface border-udl-gray-800 text-udl-white"
                    placeholder="Ej: Bebida refrescante con gas"
                    data-testid="denomination-input"
                  />
                </div>
                <div>
                  <Label className="text-udl-gray-400 mb-2 block">Contenido neto</Label>
                  <Input 
                    value={formData.brand.net_content}
                    onChange={(e) => updateFormData("brand", "net_content", e.target.value)}
                    className="bg-udl-surface border-udl-gray-800 text-udl-white"
                    placeholder="Ej: 330 ml"
                    data-testid="net-content-input"
                  />
                </div>
                <div>
                  <Label className="text-udl-gray-400 mb-2 block">País de origen</Label>
                  <Input 
                    value={formData.brand.country_origin}
                    onChange={(e) => updateFormData("brand", "country_origin", e.target.value)}
                    className="bg-udl-surface border-udl-gray-800 text-udl-white"
                    placeholder="Ej: España"
                    data-testid="country-origin-input"
                  />
                </div>
                <div>
                  <Label className="text-udl-gray-400 mb-2 block">Código EAN (si disponible)</Label>
                  <Input 
                    value={formData.brand.ean_code}
                    onChange={(e) => updateFormData("brand", "ean_code", e.target.value)}
                    className="bg-udl-surface border-udl-gray-800 text-udl-white"
                    placeholder="Ej: 8412345678901"
                    data-testid="ean-input"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-8" data-testid="step-5-content">
            {/* Volume */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label className="text-udl-gray-400 mb-2 block">MOQ deseado (primera producción)</Label>
                <Select 
                  value={formData.production.moq} 
                  onValueChange={(v) => updateFormData("production", "moq", v)}
                >
                  <SelectTrigger className="bg-udl-surface border-udl-gray-800 text-udl-white" data-testid="moq-select">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent className="bg-udl-surface border-udl-gray-800">
                    <SelectItem value="500-1000">500 - 1.000 unidades</SelectItem>
                    <SelectItem value="1000-3000">1.000 - 3.000 unidades</SelectItem>
                    <SelectItem value="3000-5000">3.000 - 5.000 unidades</SelectItem>
                    <SelectItem value="5000-10000">5.000 - 10.000 unidades</SelectItem>
                    <SelectItem value="10000+">Más de 10.000 unidades</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-udl-gray-400 mb-2 block">Primer lote objetivo</Label>
                <Input 
                  value={formData.production.first_batch}
                  onChange={(e) => updateFormData("production", "first_batch", e.target.value)}
                  className="bg-udl-surface border-udl-gray-800 text-udl-white"
                  placeholder="Ej: 2.000 unidades"
                  data-testid="first-batch-input"
                />
              </div>
              <div>
                <Label className="text-udl-gray-400 mb-2 block">Volumen anual estimado</Label>
                <Select 
                  value={formData.production.annual_volume} 
                  onValueChange={(v) => updateFormData("production", "annual_volume", v)}
                >
                  <SelectTrigger className="bg-udl-surface border-udl-gray-800 text-udl-white" data-testid="annual-volume-select">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent className="bg-udl-surface border-udl-gray-800">
                    <SelectItem value="<5000">Menos de 5.000 unidades</SelectItem>
                    <SelectItem value="5000-20000">5.000 - 20.000 unidades</SelectItem>
                    <SelectItem value="20000-50000">20.000 - 50.000 unidades</SelectItem>
                    <SelectItem value="50000-100000">50.000 - 100.000 unidades</SelectItem>
                    <SelectItem value="100000+">Más de 100.000 unidades</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-udl-gray-400 mb-2 block">Fecha objetivo de lanzamiento</Label>
                <Input 
                  type="date"
                  value={formData.production.target_date}
                  onChange={(e) => updateFormData("production", "target_date", e.target.value)}
                  className="bg-udl-surface border-udl-gray-800 text-udl-white"
                  data-testid="target-date-input"
                />
              </div>
            </div>

            {/* Sales Channel */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label className="text-udl-gray-400 mb-2 block">Canal de venta principal</Label>
                <Select 
                  value={formData.production.sales_channel} 
                  onValueChange={(v) => updateFormData("production", "sales_channel", v)}
                >
                  <SelectTrigger className="bg-udl-surface border-udl-gray-800 text-udl-white" data-testid="sales-channel-select">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent className="bg-udl-surface border-udl-gray-800">
                    <SelectItem value="horeca">HORECA (bares, restaurantes)</SelectItem>
                    <SelectItem value="retail">Retail / Supermercados</SelectItem>
                    <SelectItem value="ecommerce">E-commerce / D2C</SelectItem>
                    <SelectItem value="specialized">Tiendas especializadas</SelectItem>
                    <SelectItem value="vending">Vending</SelectItem>
                    <SelectItem value="mixed">Mixto / Multicanal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-udl-gray-400 mb-2 block">Precio objetivo (PVP)</Label>
                <Input 
                  value={formData.production.target_price}
                  onChange={(e) => updateFormData("production", "target_price", e.target.value)}
                  className="bg-udl-surface border-udl-gray-800 text-udl-white"
                  placeholder="Ej: 2.50€"
                  data-testid="target-price-input"
                />
              </div>
            </div>

            {/* Logistics */}
            <div>
              <Label className="text-udl-gray-400 mb-2 block">Países de distribución</Label>
              <Input 
                value={formData.production.countries.join(", ")}
                onChange={(e) => updateFormData("production", "countries", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
                className="bg-udl-surface border-udl-gray-800 text-udl-white"
                placeholder="España, Portugal, Francia"
                data-testid="distribution-countries-input"
              />
            </div>

            <div>
              <Label className="text-udl-gray-400 mb-2 block">Requisitos logísticos especiales</Label>
              <Textarea 
                value={formData.production.logistics_requirements}
                onChange={(e) => updateFormData("production", "logistics_requirements", e.target.value)}
                className="bg-udl-surface border-udl-gray-800 text-udl-white min-h-[80px]"
                placeholder="Ej: Necesito paletizado, almacenamiento en frío, etc."
                data-testid="logistics-textarea"
              />
            </div>

            <div>
              <Label className="text-udl-gray-400 mb-2 block">Notas para fábrica</Label>
              <Textarea 
                value={formData.production.factory_notes}
                onChange={(e) => updateFormData("production", "factory_notes", e.target.value)}
                className="bg-udl-surface border-udl-gray-800 text-udl-white min-h-[80px]"
                placeholder="Cualquier información adicional relevante para la producción..."
                data-testid="factory-notes-textarea"
              />
            </div>

            {/* Contact Info */}
            <div className="border-t border-udl-gray-800 pt-8 mt-8">
              <h3 className="text-udl-white font-heading font-bold text-lg mb-6">Datos de contacto</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-udl-gray-400 mb-2 block">Nombre completo *</Label>
                  <Input 
                    value={formData.contact_name}
                    onChange={(e) => updateFormData(null, "contact_name", e.target.value)}
                    className="bg-udl-surface border-udl-gray-800 text-udl-white"
                    placeholder="Tu nombre"
                    required
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <Label className="text-udl-gray-400 mb-2 block">Email *</Label>
                  <Input 
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => updateFormData(null, "contact_email", e.target.value)}
                    className="bg-udl-surface border-udl-gray-800 text-udl-white"
                    placeholder="tu@email.com"
                    required
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <Label className="text-udl-gray-400 mb-2 block">Teléfono</Label>
                  <Input 
                    value={formData.contact_phone}
                    onChange={(e) => updateFormData(null, "contact_phone", e.target.value)}
                    className="bg-udl-surface border-udl-gray-800 text-udl-white"
                    placeholder="+34 600 000 000"
                    data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <Label className="text-udl-gray-400 mb-2 block">Empresa</Label>
                  <Input 
                    value={formData.contact_company}
                    onChange={(e) => updateFormData(null, "contact_company", e.target.value)}
                    className="bg-udl-surface border-udl-gray-800 text-udl-white"
                    placeholder="Nombre de tu empresa"
                    data-testid="contact-company-input"
                  />
                </div>
              </div>
            </div>

            {/* Consent */}
            <div className="border-t border-udl-gray-800 pt-8 space-y-4">
              <div className="flex items-start gap-3">
                <Checkbox 
                  id="data_consent"
                  checked={formData.data_consent}
                  onCheckedChange={(v) => updateFormData(null, "data_consent", v)}
                  data-testid="data-consent-checkbox"
                />
                <Label htmlFor="data_consent" className="text-udl-gray-400 text-sm cursor-pointer leading-relaxed">
                  Acepto el tratamiento de mis datos personales para la gestión de este proyecto. *
                </Label>
              </div>
              <div className="flex items-start gap-3">
                <Checkbox 
                  id="nda_accepted"
                  checked={formData.nda_accepted}
                  onCheckedChange={(v) => updateFormData(null, "nda_accepted", v)}
                  data-testid="nda-checkbox"
                />
                <Label htmlFor="nda_accepted" className="text-udl-gray-400 text-sm cursor-pointer leading-relaxed">
                  Deseo firmar un NDA para proteger la información confidencial de mi proyecto.
                </Label>
              </div>
            </div>

            {/* Warnings */}
            {warnings.length > 0 && (
              <div className="p-4 bg-yellow-500/10 border border-yellow-500/30">
                <div className="flex items-center gap-2 text-yellow-400 mb-2">
                  <AlertTriangle size={18} />
                  <span className="font-medium">Alertas de compatibilidad</span>
                </div>
                {warnings.map((w, i) => (
                  <p key={i} className="text-yellow-400/80 text-sm">{w}</p>
                ))}
              </div>
            )}

            {/* Submit */}
            {!projectId || formData.is_draft !== false ? (
              <div className="pt-6 flex gap-4">
                <button
                  type="button"
                  onClick={() => saveProject(true)}
                  className="btn-secondary flex items-center gap-2"
                  data-testid="save-draft-btn"
                >
                  <Save size={18} />
                  Guardar borrador
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting || !formData.contact_name || !formData.contact_email || !formData.data_consent}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                  data-testid="submit-project-btn"
                >
                  {submitting ? <Loader2 className="animate-spin" size={18} /> : <Factory size={18} />}
                  {submitting ? "Enviando..." : "Enviar proyecto"}
                </button>
              </div>
            ) : (
              <div className="bg-udl-primary/10 border border-udl-primary p-6" data-testid="success-message">
                <h4 className="text-udl-primary font-heading font-bold text-lg mb-2">Proyecto enviado correctamente</h4>
                <p className="text-udl-gray-400 text-sm mb-4">
                  Tu dossier técnico está listo. Nuestro equipo lo revisará y te contactará pronto.
                </p>
                <button
                  type="button"
                  onClick={downloadDossier}
                  disabled={loading}
                  className="btn-primary flex items-center gap-2"
                  data-testid="download-dossier-btn"
                >
                  {loading ? <Loader2 className="animate-spin" size={18} /> : <Download size={18} />}
                  {loading ? "Descargando..." : "Descargar dossier PDF"}
                </button>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <main className="min-h-screen pt-20" data-testid="configurator-page">
      <div className="flex flex-col lg:flex-row">
        {/* Sidebar - Steps */}
        <aside className="w-full lg:w-72 bg-udl-bg border-b lg:border-b-0 lg:border-r border-udl-gray-800 p-4 lg:p-6 lg:min-h-[calc(100vh-80px)]" data-testid="steps-sidebar">
          <div className="flex lg:flex-col gap-2 lg:gap-0 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
            {steps.map((step) => (
              <button
                key={step.id}
                onClick={() => setCurrentStep(step.id)}
                data-testid={`step-${step.id}-btn`}
                className={`flex items-center gap-3 py-3 lg:py-4 px-3 lg:px-0 lg:border-l-2 lg:pl-4 whitespace-nowrap transition-colors min-w-max lg:min-w-0 ${
                  currentStep === step.id
                    ? "text-udl-primary lg:border-udl-primary font-bold bg-udl-primary/5 lg:bg-transparent"
                    : currentStep > step.id
                    ? "text-udl-primary/70 lg:border-udl-primary/50"
                    : "text-udl-gray-700 lg:border-udl-gray-800 hover:text-udl-gray-400"
                }`}
              >
                <div className={`w-8 h-8 flex items-center justify-center border ${
                  currentStep === step.id 
                    ? "border-udl-primary bg-udl-primary/10" 
                    : currentStep > step.id 
                    ? "border-udl-primary/50 bg-udl-primary/5" 
                    : "border-udl-gray-800"
                }`}>
                  {currentStep > step.id ? (
                    <Check size={16} className="text-udl-primary" />
                  ) : (
                    <step.icon size={16} />
                  )}
                </div>
                <div className="text-left hidden lg:block">
                  <span className="text-sm block">{step.name}</span>
                  <span className="text-xs text-udl-gray-700">{step.desc}</span>
                </div>
                <span className="lg:hidden text-sm">{step.name}</span>
              </button>
            ))}
          </div>

          {/* Progress indicator */}
          <div className="hidden lg:block mt-8 pt-8 border-t border-udl-gray-800">
            <div className="flex justify-between text-xs text-udl-gray-400 mb-2">
              <span>Progreso</span>
              <span>{Math.round((currentStep / steps.length) * 100)}%</span>
            </div>
            <div className="h-1 bg-udl-gray-800 overflow-hidden">
              <div 
                className="h-full bg-udl-primary transition-all duration-300"
                style={{ width: `${(currentStep / steps.length) * 100}%` }}
              />
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1 p-4 lg:p-12">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <span className="text-udl-primary text-xs font-mono uppercase tracking-widest mb-2 block">
                Paso {currentStep} de {steps.length}
              </span>
              <h1 className="font-heading text-2xl lg:text-4xl font-bold text-udl-white">
                {steps[currentStep - 1].name}
              </h1>
              <p className="text-udl-gray-400 text-sm mt-2">{steps[currentStep - 1].desc}</p>
            </div>

            {/* Step Content */}
            {renderStep()}

            {/* Navigation */}
            {(currentStep < 5 || (projectId && formData.is_draft !== false)) && (
              <div className="flex justify-between mt-12 pt-8 border-t border-udl-gray-800">
                <button
                  type="button"
                  onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
                  disabled={currentStep === 1}
                  className={`flex items-center gap-2 ${
                    currentStep === 1 ? "text-udl-gray-700 cursor-not-allowed" : "text-udl-gray-400 hover:text-udl-white"
                  }`}
                  data-testid="prev-step-btn"
                >
                  <ChevronLeft size={18} />
                  Anterior
                </button>
                {currentStep < steps.length && (
                  <button
                    type="button"
                    onClick={() => setCurrentStep(Math.min(steps.length, currentStep + 1))}
                    className="btn-secondary flex items-center gap-2"
                    data-testid="next-step-btn"
                  >
                    Siguiente
                    <ChevronRight size={18} />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
